import { pick } from '../pick';
import { unwrapApiItems, unwrapListItems } from '../../api/unwrap';

export const getVisibleProcurementClauses = (procurement = {}) =>
  [...((procurement ?? {}).procurementClauseDetails ?? [])]
    .filter(clause => clause.isShow === true)
    .sort((a, b) => (a.ordreNumber ?? 0) - (b.ordreNumber ?? 0));

export const hasClauseResponse = value => Boolean(String(value ?? '').trim());

export const toLockedFlag = value =>
  value === true || value === 1 || value === '1' || value === 'true';

export const expectIsPertaminaFromView = isEvaluatorView => !isEvaluatorView;

export const isUnreadForRole = (item, expectIsPertamina) =>
  Boolean(item?.id) &&
  item.isRead !== true &&
  toPertaminaFlag(item.isPertamina) === expectIsPertamina;

export const matchesRespondMitra = (
  item,
  procurementMitraId,
  { allowUnscoped = false } = {}
) => {
  if (!procurementMitraId) return true;
  if (item?.procurementMitraId == null) return allowUnscoped === true;
  return String(item.procurementMitraId) === String(procurementMitraId);
};

export const unwrapRespondListItems = payload => {
  const fromApi = unwrapApiItems(payload);
  if (fromApi.length > 0) return fromApi;
  return unwrapListItems(payload);
};

export const filterRespondItemsByMitra = (
  items,
  procurementMitraId,
  { scopedQuery = false } = {}
) => {
  const list = items ?? [];
  if (!procurementMitraId || scopedQuery) return list;
  return list.filter(item =>
    matchesRespondMitra(item, procurementMitraId, { allowUnscoped: true })
  );
};

export const hasUnreadRespondsForRole = (items, isEvaluatorView = true) => {
  const expectIsPertamina = expectIsPertaminaFromView(isEvaluatorView);
  return (items ?? []).some(item => isUnreadForRole(item, expectIsPertamina));
};

export const normalizeRespondClauseDetail = raw => {
  if (!raw || typeof raw !== 'object') return null;

  const procurementClauseDetailId = pick(
    raw,
    'procurementClauseDetailId',
    'ProcurementClauseDetailId'
  );

  if (!procurementClauseDetailId) return null;

  return {
    id: pick(raw, 'id', 'Id'),
    procurementMitraId: pick(raw, 'procurementMitraId', 'ProcurementMitraId'),
    procurementClauseDetailId,
    respondNotes: pick(raw, 'respondNotes', 'RespondNotes') ?? '',
    isPertamina: pick(raw, 'isPertamina', 'IsPertamina'),
    createdByLabel: pick(raw, 'createdByLabel', 'CreatedByLabel') ?? '',
    createdDate: pick(raw, 'createdDate', 'CreatedDate'),
    updatedDate: pick(raw, 'updatedDate', 'UpdatedDate'),
    isLocked: toLockedFlag(pick(raw, 'isLocked', 'IsLocked')),
    isRead: pick(raw, 'isRead', 'IsRead') === true,
  };
};

export const TYPE_RESPONSE_TOLERANCE = 1;
export const TYPE_RESPONSE_VALIDITY = 2;
export const TYPE_RESPONSE_DOCUMENT = 3;
export const TYPE_RESPONSE_GLOBAL = 4;
export const TYPE_RESPONSE_COUNTER_PAYMENT_TERM = 5;
export const TYPE_RESPONSE_COUNTER_COMPLIANCE = 6;

export const normalizeRespondOperationalToleranceValidity = raw => {
  if (!raw || typeof raw !== 'object') return null;

  const procurementId = pick(raw, 'procurementId', 'ProcurementId');
  const typeResponse = Number(pick(raw, 'typeResponse', 'TypeResponse'));

  if (!procurementId || !Number.isFinite(typeResponse)) return null;

  return {
    id: pick(raw, 'id', 'Id'),
    procurementId,
    procurementMitraId: pick(raw, 'procurementMitraId', 'ProcurementMitraId'),
    respondNotes: pick(raw, 'respondNotes', 'RespondNotes') ?? '',
    typeResponse,
    isPertamina: pick(raw, 'isPertamina', 'IsPertamina'),
    createdByLabel: pick(raw, 'createdByLabel', 'CreatedByLabel') ?? '',
    createdDate: pick(raw, 'createdDate', 'CreatedDate'),
    updatedDate: pick(raw, 'updatedDate', 'UpdatedDate'),
    isResponded: pick(raw, 'isResponded', 'IsResponded') === true,
    isLocked: toLockedFlag(pick(raw, 'isLocked', 'IsLocked')),
    isRead: pick(raw, 'isRead', 'IsRead') === true,
  };
};

export const getResponseSortTime = item => {
  const updated = item?.updatedDate
    ? new Date(item.updatedDate).getTime()
    : NaN;
  const created = item?.createdDate
    ? new Date(item.createdDate).getTime()
    : NaN;
  if (!Number.isNaN(updated)) return updated;
  if (!Number.isNaN(created)) return created;
  return Number.POSITIVE_INFINITY;
};

export const stampSavedResponse = (saved, { isEvaluator = false } = {}) => {
  if (!saved) return null;
  const now = new Date().toISOString();
  const hasFlag = saved.isPertamina === true || saved.isPertamina === false;
  return {
    ...saved,
    isPertamina: hasFlag ? saved.isPertamina : Boolean(isEvaluator),
    createdDate: saved.createdDate ?? now,
    updatedDate: saved.updatedDate ?? now,
  };
};

export const sortRespondOperationalToleranceValidities = items =>
  [...(items ?? [])].sort(
    (a, b) => getResponseSortTime(a) - getResponseSortTime(b)
  );

export const mapResponsesByTypeResponse = items => {
  const acc = {
    [TYPE_RESPONSE_TOLERANCE]: [],
    [TYPE_RESPONSE_VALIDITY]: [],
    [TYPE_RESPONSE_DOCUMENT]: [],
    [TYPE_RESPONSE_GLOBAL]: [],
    [TYPE_RESPONSE_COUNTER_PAYMENT_TERM]: [],
    [TYPE_RESPONSE_COUNTER_COMPLIANCE]: [],
  };

  (items ?? []).forEach(item => {
    if (!item || item.typeResponse == null) return;
    const key = Number(item.typeResponse);
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
  });

  Object.keys(acc).forEach(key => {
    acc[key] = sortRespondOperationalToleranceValidities(acc[key]);
  });

  return acc;
};

export const toClauseResponseList = value => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return [value];
  return [];
};

export const toToleranceValidityHistoryItems = (responses = []) =>
  toClauseResponseList(responses)
    .filter(item => hasClauseResponse(item?.respondNotes))
    .map(item => ({
      ...item,
      display: item.respondNotes,
    }));

export const sortRespondClauseDetails = items =>
  [...(items ?? [])].sort(
    (a, b) => getResponseSortTime(a) - getResponseSortTime(b)
  );

export const mapResponsesByClauseId = items => {
  const acc = {};

  (items ?? []).forEach(item => {
    if (!item?.procurementClauseDetailId) return;
    const key = item.procurementClauseDetailId;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
  });

  Object.keys(acc).forEach(key => {
    acc[key] = sortRespondClauseDetails(acc[key]);
  });

  return acc;
};

export const toPertaminaFlag = value =>
  value === true || value === 1 || value === '1' || value === 'true';

export const isEvaluatorResponse = response =>
  toPertaminaFlag(response?.isPertamina);

export const getOwnClauseResponse = (
  responses,
  { isEvaluator = false } = {}
) => {
  const list = toClauseResponseList(responses);
  if (!list.length) return null;

  if (isEvaluator) {
    return [...list].reverse().find(item => isEvaluatorResponse(item)) ?? null;
  }

  return [...list].reverse().find(item => !isEvaluatorResponse(item)) ?? null;
};

export const mapOwnResponsesByClauseId = (
  items,
  { isEvaluator = false } = {}
) => {
  const grouped = mapResponsesByClauseId(items);
  return Object.keys(grouped).reduce((acc, key) => {
    acc[key] = getOwnClauseResponse(grouped[key], { isEvaluator });
    return acc;
  }, {});
};

export const isLastResponseEvaluator = responses => {
  const list = sortRespondClauseDetails(
    toClauseResponseList(responses).filter(item =>
      hasClauseResponse(item?.respondNotes)
    )
  );
  if (!list.length) return false;
  return isEvaluatorResponse(list[list.length - 1]);
};

export const needsIcReplyOnEvaluatorThread = responses => {
  const list = sortRespondClauseDetails(
    toClauseResponseList(responses).filter(item =>
      hasClauseResponse(item?.respondNotes)
    )
  );
  if (!list.length) return false;
  const last = list[list.length - 1];
  if (toLockedFlag(last?.isLocked)) return false;
  return isEvaluatorResponse(last);
};

export const getLastOwnEditableResponse = (
  responses,
  { isEvaluator = false } = {}
) => {
  const list = sortRespondClauseDetails(
    toClauseResponseList(responses).filter(item =>
      hasClauseResponse(item?.respondNotes)
    )
  );
  if (!list.length) return null;
  if (!isEvaluator && !list.some(isEvaluatorResponse)) return null;
  const last = list[list.length - 1];
  if (toLockedFlag(last?.isLocked)) return null;
  if (isEvaluatorResponse(last) !== isEvaluator) return null;
  return last;
};

export const canAgreeLastCounter = (responses, item) => {
  if (!item) return false;
  const list = sortRespondClauseDetails(
    toClauseResponseList(responses).filter(entry =>
      hasClauseResponse(entry?.respondNotes)
    )
  );
  if (!list.length) return false;
  const last = list[list.length - 1];
  if (isEvaluatorResponse(last)) return false;
  return String(last.id) === String(item.id);
};

export const needsTeAgreeOnLastCounter = responses => {
  const list = sortRespondClauseDetails(
    toClauseResponseList(responses).filter(item =>
      hasClauseResponse(item?.respondNotes)
    )
  );
  if (!list.length) return false;
  const last = list[list.length - 1];
  if (toLockedFlag(last?.isLocked)) return false;
  return !isEvaluatorResponse(last);
};

/** TE Send Clarification: last message must be unlocked evaluator respond. */
export const hasTeClarificationRespondReady = responses => {
  const list = sortRespondClauseDetails(
    toClauseResponseList(responses).filter(item =>
      hasClauseResponse(item?.respondNotes)
    )
  );
  if (!list.length) return false;
  const last = list[list.length - 1];
  if (toLockedFlag(last?.isLocked)) return false;
  return isEvaluatorResponse(last);
};
